"use client";
import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { TransformControls } from "three/addons/controls/TransformControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { RGBELoader } from "three/addons/loaders/RGBELoader.js";
import { GLTFExporter } from "three/addons/exporters/GLTFExporter.js";
import { useData } from "../context/Context";
import { BASE_URL_FLASK, BASE_URL_NODE } from "../services/serviceUrls";

export default function ThreeDEditor({ model_source, close }) {
  const {
    state: { selected_model, selected_table },
    setState,
  } = useData();
  const username = localStorage.getItem("username");

  const containerRef = useRef();
  const modelRef = useRef();
  const cameraRef = useRef(null);
  const [debugText, setDebugText] = useState(
    "View: perspective | Mode: translate"
  );
  const gizmosRef = useRef({
    gizmoPersp: null,
    gizmoSide: null,
    gizmoTop: null,
    gizmoFront: null,
  });
  const [transformAction, setTransformAction] = useState("");
  const [keyPressed, setKeyPressed] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState("");
  const [isCapturingPreviews, setIsCapturingPreviews] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const [previewMessage, setPreviewMessage] = useState("");
  const [previewUrls, setPreviewUrls] = useState([]);

  const sceneRef = useRef(null);
  const mannyRef = useRef();
  const rendererRef = useRef(null);
  const controlsRef = useRef({});
  const animationFrameRef = useRef(null);
  useEffect(() => {
    const loader = new GLTFLoader();
    loader.load("/Manny.glb", (gltf) => {
      const manny = gltf.scene;
      mannyRef.current = manny;
      sceneRef.current?.add(manny);
    });
  }, []);
  useEffect(() => {
    const container = containerRef.current;

    let currentMode = "translate";
    let activeViewName = "perspective";

    function updateDebugText() {
      setDebugText(`View: ${activeViewName} | Mode: ${currentMode}`);
    }
    updateDebugText();

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    rendererRef.current = renderer;
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    scene.background = new THREE.Color("#3f3f3f");

    new RGBELoader().load("pretoria_gardens_1k.hdr", (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      scene.environment = texture;
    });

    // Add improved lighting setup
    // Ambient light for overall brightness
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    // Key light (main directional light)
    const keyLight = new THREE.DirectionalLight(0xffffff, 1.0);
    keyLight.position.set(10, 10, 5);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.width = 1024;
    keyLight.shadow.mapSize.height = 1024;
    scene.add(keyLight);

    // Fill light (softer light from opposite side)
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.6);
    fillLight.position.set(-5, 8, 10);
    scene.add(fillLight);

    // Rim light (back light for edge definition)
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.8);
    rimLight.position.set(0, 5, -10);
    scene.add(rimLight);

    // Additional side lights for better coverage
    const sideLight1 = new THREE.DirectionalLight(0xffffff, 0.4);
    sideLight1.position.set(15, 3, 0);
    scene.add(sideLight1);

    const sideLight2 = new THREE.DirectionalLight(0xffffff, 0.4);
    sideLight2.position.set(-15, 3, 0);
    scene.add(sideLight2);

    const perspectiveCamera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    perspectiveCamera.position.set(0, 5, 10);
    perspectiveCamera.lookAt(0, 0, 0);
    cameraRef.current = perspectiveCamera;

    const frustumSize = 10;
    const aspect = window.innerWidth / window.innerHeight;
    const orthoWidth = (frustumSize * aspect) / 2;
    const orthoHeight = frustumSize / 2;
    const orthoTopView = new THREE.OrthographicCamera(
      -orthoWidth,
      orthoWidth,
      orthoHeight,
      -orthoHeight,
      0.1,
      1000
    );
    orthoTopView.position.set(0, 10, 0);
    orthoTopView.up.set(0, 0, -1);
    orthoTopView.lookAt(0, 0, 0);

    const orthoSideView = new THREE.OrthographicCamera(
      -orthoWidth,
      orthoWidth,
      orthoHeight,
      -orthoHeight,
      0.1,
      1000
    );
    orthoSideView.position.set(10, 0, 0);
    orthoSideView.lookAt(0, 0, 0);

    const orthoFrontView = new THREE.OrthographicCamera(
      -orthoWidth,
      orthoWidth,
      orthoHeight,
      -orthoHeight,
      0.1,
      1000
    );
    orthoFrontView.position.set(0, 0, 10);
    orthoFrontView.lookAt(0, 0, 0);

    const topControls = new OrbitControls(
      perspectiveCamera,
      renderer.domElement
    );
    const orthoTopControls = new OrbitControls(
      orthoTopView,
      renderer.domElement
    );
    orthoTopControls.enableRotate = false;
    const orthoSideControls = new OrbitControls(
      orthoSideView,
      renderer.domElement
    );
    orthoSideControls.enableRotate = false;

    const orthoFrontControls = new OrbitControls(
      orthoFrontView,
      renderer.domElement
    );
    orthoFrontControls.enableRotate = false;

    const transformControlsPersp = new TransformControls(
      perspectiveCamera,
      renderer.domElement
    );
    const transformControlsSide = new TransformControls(
      orthoSideView,
      renderer.domElement
    );
    const transformControlsTop = new TransformControls(
      orthoTopView,
      renderer.domElement
    );
    const transformControlsFront = new TransformControls(
      orthoFrontView,
      renderer.domElement
    );

    controlsRef.current = {
      topControls,
      orthoTopControls,
      orthoSideControls,
      orthoFrontControls,
      transformControlsPersp,
      transformControlsSide,
      transformControlsTop,
      transformControlsFront,
    };

    const gizmoPersp = transformControlsPersp.getHelper();
    const gizmoSide = transformControlsSide.getHelper();
    const gizmoTop = transformControlsTop.getHelper();
    const gizmoFront = transformControlsFront.getHelper();
    // const gizmoPersp = transformControlsPersp.getHelper();
    // const gizmoSide = transformControlsSide.getHelper();
    // const gizmoTop = transformControlsTop.getHelper();
    // const gizmoFront = transformControlsFront.getHelper();
    // scene.add(gizmoPersp);
    // scene.add(gizmoSide);
    // scene.add(gizmoTop);
    // scene.add(gizmoFront);
    gizmosRef.current = {
      gizmoPersp,
      gizmoSide,
      gizmoTop,
      gizmoFront,
    };
    function applyUniformScaling() {
      if (this.mode === "scale") {
        const s = this.object.scale;
        const avg = (s.x + s.y + s.z) / 3;
        this.object.scale.set(avg, avg, avg);
      }
    }
    [
      transformControlsPersp,
      transformControlsSide,
      transformControlsTop,
      transformControlsFront,
    ].forEach((tc) => {
      tc.addEventListener("objectChange", applyUniformScaling);
    });

    const transformViews = {
      perspective: {
        camera: perspectiveCamera,
        control: transformControlsPersp,
      },
      side: { camera: orthoSideView, control: transformControlsSide },
      top: { camera: orthoTopView, control: transformControlsTop },
      front: { camera: orthoFrontView, control: transformControlsFront },
    };

    const transformHistory = [];
    function storeTransform(object) {
      transformHistory.push({
        position: object.position.clone(),
        rotation: object.rotation.clone(),
        scale: object.scale.clone(),
      });
    }

    function onDraggingChanged(event) {
      topControls.enabled = !event.value;
      orthoTopControls.enabled = !event.value;
      orthoSideControls.enabled = !event.value;
      orthoFrontControls.enabled = !event.value;
    }

    function attachTransformListeners(model) {
      [
        transformControlsPersp,
        transformControlsSide,
        transformControlsTop,
        transformControlsFront,
      ].forEach((tc) => {
        tc.addEventListener("mouseDown", () => storeTransform(model));
        tc.addEventListener("dragging-changed", onDraggingChanged);
      });
    }

    function loadGLBModel(filePath) {
      console.log("Loading model from:", filePath);

      if (modelRef.current) {
        transformControlsPersp.detach();
        transformControlsSide.detach();
        transformControlsTop.detach();
        transformControlsFront.detach();
        scene.remove(modelRef.current);
      }

      new GLTFLoader().load(filePath, (gltf) => {
        const rawModel = gltf.scene;

        if (!filePath.includes("/aligned/")) {
          const box = new THREE.Box3().setFromObject(rawModel);
          const center = new THREE.Vector3();
          box.getCenter(center);

          rawModel.traverse((child) => {
            if (child.isMesh) {
              child.geometry.translate(-center.x, -center.y, -center.z);
            }
          });
        }

        const box = new THREE.Box3().setFromObject(rawModel);
        const yShift = -box.min.y;

        const container = new THREE.Object3D();
        container.position.set(0, 0, 0);
        container.add(rawModel);

        modelRef.current = container;
        scene.add(modelRef.current);
        modelRef.current.updateMatrixWorld(true);

        const boundingBox = new THREE.Box3().setFromObject(container);
        const extentFromOrigin = {
          x_negative: Math.abs(boundingBox.min.x).toFixed(3),
          x_positive: Math.abs(boundingBox.max.x).toFixed(3),
          y_negative: Math.abs(boundingBox.min.y).toFixed(3),
          y_positive: Math.abs(boundingBox.max.y).toFixed(3),
          z_negative: Math.abs(boundingBox.min.z).toFixed(3),
          z_positive: Math.abs(boundingBox.max.z).toFixed(3),
        };
        console.log(
          "📐 Axis extents from origin during load:",
          extentFromOrigin
        );

        const modelWorldPos = new THREE.Vector3();
        modelRef.current.getWorldPosition(modelWorldPos);
        console.log("📍 Model opens at position:", modelWorldPos);

        transformControlsPersp.attach(modelRef.current);
        transformControlsSide.attach(modelRef.current);
        transformControlsTop.attach(modelRef.current);
        transformControlsFront.attach(modelRef.current);

        attachTransformListeners(modelRef.current);
      });
    }

    let activeCamera = perspectiveCamera;
    function updateGizmos() {
      scene.remove(gizmoPersp, gizmoSide, gizmoTop, gizmoFront);
      if (activeCamera === perspectiveCamera) {
        scene.add(gizmoPersp);
      } else if (activeCamera === orthoSideView) {
        scene.add(gizmoSide);
      } else if (activeCamera === orthoTopView) {
        scene.add(gizmoTop);
      } else if (activeCamera === orthoFrontView) {
        scene.add(gizmoFront);
      }
    }
    updateGizmos();

    function setTransformControlsMode(mode) {
      currentMode = mode;
      Object.values(transformViews).forEach(({ control }) =>
        control.setMode(mode)
      );
      updateDebugText();
    }

    function setTransformControlsView(viewName) {
      const view = transformViews[viewName];
      if (view) {
        activeViewName = viewName;
        activeCamera = view.camera;
        Object.keys(transformViews).forEach((key) => {
          transformViews[key].control.enabled = key === viewName;
        });
        updateDebugText();
        updateGizmos();
      }
    }

    function handleKeyDown(event) {
      if (keyPressed) return;
      setKeyPressed(true);

      switch (event.key) {
        case "q":
          setTransformControlsMode("translate");
          break;
        case "w":
          setTransformControlsMode("rotate");
          break;
        case "e":
          setTransformControlsMode("scale");
          break;
        case "1":
          if (!window.transformInputState)
            setTransformControlsView("perspective");
          break;
        case "2":
          if (!window.transformInputState) setTransformControlsView("side");
          break;
        case "3":
          if (!window.transformInputState) setTransformControlsView("top");
          break;
        case "4":
          if (!window.transformInputState) setTransformControlsView("front");
          break;
      }
      if (event.ctrlKey && event.key === "z") {
        if (transformHistory.length > 0) {
          const lastState = transformHistory.pop();
          if (modelRef.current) {
            modelRef.current.position.copy(lastState.position);
            modelRef.current.rotation.copy(lastState.rotation);
            modelRef.current.scale.copy(lastState.scale);
          }
        }
      }

      if (!window.transformInputState) {
        if (event.ctrlKey) return;

        if (
          ["A", "S", "D", "Z", "X", "C", "R"].includes(event.key.toUpperCase())
        ) {
          const keyMap = {
            A: { type: "translate", axis: "x", label: "Translating X" },
            S: { type: "translate", axis: "y", label: "Translating Y" },
            D: { type: "translate", axis: "z", label: "Translating Z" },
            Z: { type: "rotate", axis: "x", label: "Rotating X" },
            X: { type: "rotate", axis: "y", label: "Rotating Y" },
            C: { type: "rotate", axis: "z", label: "Rotating Z" },
            R: { type: "scale", axis: null, label: "Scaling Uniformly" },
          };
          const { type, axis, label } = keyMap[event.key.toUpperCase()];

          storeTransform(modelRef.current);

          if (modelRef.current) {
            setTransformAction(label);
            window.transformInputState = {
              mode: type,
              axis: axis,
              sign: 1,
              valueStr: "",
              originalPosition: modelRef.current.position.clone(),
              originalRotation: modelRef.current.rotation.clone(),
              originalScale: modelRef.current.scale.clone(),
            };
            [
              transformControlsPersp,
              transformControlsSide,
              transformControlsTop,
            ].forEach((tc) => tc.detach());
          }
        }
      } else {
        const st = window.transformInputState;
        if (event.key === "Enter") {
          [
            transformControlsPersp,
            transformControlsSide,
            transformControlsTop,
          ].forEach((tc) => tc.attach(modelRef.current));
          setTransformAction("");
          delete window.transformInputState;
        } else if (event.key === "`") {
          modelRef.current.position.copy(st.originalPosition);
          modelRef.current.rotation.copy(st.originalRotation);
          modelRef.current.scale.copy(st.originalScale);
          [
            transformControlsPersp,
            transformControlsSide,
            transformControlsTop,
          ].forEach((tc) => tc.attach(modelRef.current));
          setTransformAction("");
          delete window.transformInputState;
        } else if (event.key === "Backspace") {
          st.valueStr = st.valueStr.slice(0, -1);
          applyNumericTransform(st);
        } else if (event.key === "-") {
          st.sign *= -1;
          applyNumericTransform(st);
        } else if (event.key === ".") {
          if (!st.valueStr.includes(".")) {
            if (st.valueStr === "") st.valueStr = "0";
            st.valueStr += ".";
            applyNumericTransform(st);
          }
        } else if (/\d/.test(event.key)) {
          st.valueStr += event.key;
          applyNumericTransform(st);
        }
      }
    }

    function handleKeyUp() {
      setKeyPressed(false);
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    function applyNumericTransform(st) {
      modelRef.current.position.copy(st.originalPosition);
      modelRef.current.rotation.copy(st.originalRotation);
      modelRef.current.scale.copy(st.originalScale);

      if (!st.valueStr) return;
      let val = parseFloat(st.valueStr) * st.sign;

      switch (st.mode) {
        case "translate":
          modelRef.current.position[st.axis] += val;
          break;
        case "rotate":
          modelRef.current.rotation[st.axis] += THREE.MathUtils.degToRad(val);
          break;
        case "scale":
          modelRef.current.scale.multiplyScalar(val);
          break;
      }
    }

    const grid = new THREE.GridHelper(20, 20, 0x888888, 0x888888);
    scene.add(grid);
    const originSphere = new THREE.Mesh(
      new THREE.SphereGeometry(0.05, 32, 32),
      new THREE.MeshBasicMaterial({ color: 0xffffff, depthTest: false })
    );
    scene.add(originSphere);

    const onWindowResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      renderer.setSize(width, height);
      perspectiveCamera.aspect = width / height;
      perspectiveCamera.updateProjectionMatrix();
      const newOrthoHeight = frustumSize / 2;
      const newOrthoWidth = newOrthoHeight * (width / height);
      [orthoTopView, orthoSideView, orthoFrontView].forEach((orthoCamera) => {
        orthoCamera.left = -newOrthoWidth;
        orthoCamera.right = newOrthoWidth;
        orthoCamera.top = newOrthoHeight;
        orthoCamera.bottom = -newOrthoHeight;
        orthoCamera.updateProjectionMatrix();
      });
    };
    window.addEventListener("resize", onWindowResize);

    onWindowResize();

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      topControls.update();
      orthoTopControls.update();
      orthoSideControls.update();
      renderer.setScissorTest(false);
      renderer.clear();
      renderer.render(scene, activeCamera);
    };
    animate();

    loadGLBModel(model_source);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      window.removeEventListener("resize", onWindowResize);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);

      if (controlsRef.current) {
        Object.values(controlsRef.current).forEach((control) => {
          if (control && typeof control.dispose === "function") {
            control.dispose();
          }
        });
      }

      if (modelRef.current) {
        modelRef.current.traverse((child) => {
          if (child.geometry) {
            child.geometry.dispose();
          }

          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => disposeMaterial(material));
            } else {
              disposeMaterial(child.material);
            }
          }
        });
      }

      if (sceneRef.current) {
        sceneRef.current.traverse((object) => {
          if (object.geometry) {
            object.geometry.dispose();
          }

          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => disposeMaterial(material));
            } else {
              disposeMaterial(object.material);
            }
          }
        });
      }

      if (sceneRef.current?.environment) {
        sceneRef.current.environment.dispose();
      }

      if (container && rendererRef.current) {
        container.removeChild(rendererRef.current.domElement);
        rendererRef.current.dispose();

        if (rendererRef.current.forceContextLoss) {
          rendererRef.current.forceContextLoss();
        }
      }

      sceneRef.current = null;
      rendererRef.current = null;
      controlsRef.current = {};
      modelRef.current = null;
    };
  }, []);

  function disposeMaterial(material) {
    if (!material) return;

    Object.keys(material).forEach((prop) => {
      if (!material[prop]) return;
      if (material[prop].isTexture) {
        material[prop].dispose();
      } else if (
        material[prop].map &&
        typeof material[prop].map.dispose === "function"
      ) {
        material[prop].map.dispose();
      }
    });

    const textureProps = [
      "map",
      "normalMap",
      "bumpMap",
      "displacementMap",
      "roughnessMap",
      "metalnessMap",
      "alphaMap",
      "aoMap",
      "emissiveMap",
      "envMap",
      "lightMap",
      "specularMap",
    ];

    textureProps.forEach((prop) => {
      if (material[prop] && typeof material[prop].dispose === "function") {
        material[prop].dispose();
      }
    });

    material.dispose();
  }

  const uploadModel = async () => {
    if (!modelRef.current) {
      alert("No model to upload");
      return;
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadMessage("Preparing Top View Screenshot...");

      const transformControls = [
        controlsRef.current.transformControlsPersp,
        controlsRef.current.transformControlsSide,
        controlsRef.current.transformControlsTop,
      ];

      let attachedControl = null;
      transformControls.forEach((control) => {
        if (control && control.object) {
          attachedControl = control;
          control.detach();
        }
      });

      const distance = 3;
      cameraRef.current.position.set(0, distance, 0);
      cameraRef.current.lookAt(0, 0, 0);
      cameraRef.current.updateProjectionMatrix();

      const originalWidth = rendererRef.current.domElement.width;
      const originalHeight = rendererRef.current.domElement.height;

      const size = Math.min(originalWidth, originalHeight);

      rendererRef.current.setSize(size, size);

      cameraRef.current.aspect = 1;
      cameraRef.current.updateProjectionMatrix();

      rendererRef.current.render(sceneRef.current, cameraRef.current);

      const dataURL = rendererRef.current.domElement.toDataURL("image/jpeg");

      const base64Data = dataURL.split(",")[1];

      const byteString = atob(base64Data);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: "image/jpeg" });

      const filename = `${
        selected_model.thumbnail_path.split("-")[0].split("/")[1]
      }-6.jpg`;

      try {
        setUploadMessage("Uploading Top-View Screenshot...");

        const response = await fetch(
          `${BASE_URL_FLASK}/upload_screenshot?filename=${encodeURIComponent(
            filename
          )}`
        );
        if (!response.ok) {
          throw new Error("Failed to get upload URL");
        }

        const { url } = await response.json();

        await uploadWithProgress(url, blob, "image/jpeg");

        setUploadProgress(100);
        setUploadMessage("Screenshot uploaded successfully");
      } catch (error) {
        console.error("Error uploading screenshot:", error);
        setUploadMessage(`Screenshot upload failed: ${error.message}`);
        setTimeout(() => {
          setIsUploading(false);
          setUploadMessage("");
        }, 2000);
        return;
      }

      rendererRef.current.setSize(originalWidth, originalHeight);

      cameraRef.current.aspect = originalWidth / originalHeight;
      cameraRef.current.updateProjectionMatrix();

      if (attachedControl && modelRef.current) {
        attachedControl.attach(modelRef.current);
      }
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadMessage("Preparing Front View Screenshot...");

      const transformControls = [
        controlsRef.current.transformControlsPersp,
        controlsRef.current.transformControlsSide,
        controlsRef.current.transformControlsTop,
      ];

      let attachedControl = null;
      transformControls.forEach((control) => {
        if (control && control.object) {
          attachedControl = control;
          control.detach();
        }
      });

      const frontCamera = new THREE.OrthographicCamera(
        -2,
        2, // left, right
        2,
        -2, // top, bottom
        0.1,
        1000 // near, far
      );
      frontCamera.position.set(0, 0, 4); // Positioned on positive X axis
      frontCamera.lookAt(0, 0, 0);
      frontCamera.updateProjectionMatrix();

      // Update camera reference for screenshot
      cameraRef.current = frontCamera;
      cameraRef.current.updateProjectionMatrix();

      const originalWidth = rendererRef.current.domElement.width;
      const originalHeight = rendererRef.current.domElement.height;

      const size = Math.min(originalWidth, originalHeight);

      rendererRef.current.setSize(size, size);

      cameraRef.current.aspect = 1;
      cameraRef.current.updateProjectionMatrix();

      rendererRef.current.render(sceneRef.current, cameraRef.current);

      const dataURL = rendererRef.current.domElement.toDataURL("image/jpeg");

      const base64Data = dataURL.split(",")[1];

      const byteString = atob(base64Data);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: "image/jpeg" });

      const filename = `${
        selected_model.thumbnail_path.split("-")[0].split("/")[1]
      }.jpg`;

      try {
        setUploadMessage("Uploading Front-View Screenshot......");

        const response = await fetch(
          `${BASE_URL_FLASK}/upload_screenshot?filename=${encodeURIComponent(
            filename
          )}`
        );
        if (!response.ok) {
          throw new Error("Failed to get upload URL");
        }

        const { url } = await response.json();

        await uploadWithProgress(url, blob, "image/jpeg");

        setUploadProgress(100);
        setUploadMessage("Screenshot uploaded successfully");
      } catch (error) {
        console.error("Error uploading screenshot:", error);
        setUploadMessage(`Screenshot upload failed: ${error.message}`);
        setTimeout(() => {
          setIsUploading(false);
          setUploadMessage("");
        }, 2000);
        return;
      }

      rendererRef.current.setSize(originalWidth, originalHeight);

      cameraRef.current.aspect = originalWidth / originalHeight;
      cameraRef.current.updateProjectionMatrix();

      if (attachedControl && modelRef.current) {
        attachedControl.attach(modelRef.current);
      }
    }
    ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadMessage("Preparing Side View Screenshot...");

      const transformControls = [
        controlsRef.current.transformControlsPersp,
        controlsRef.current.transformControlsSide,
        controlsRef.current.transformControlsTop,
      ];

      let attachedControl = null;
      transformControls.forEach((control) => {
        if (control && control.object) {
          attachedControl = control;
          control.detach();
        }
      });

      const frontCamera = new THREE.OrthographicCamera(
        -2,
        2, // left, right
        2,
        -2, // top, bottom
        0.1,
        1000 // near, far
      );
      frontCamera.position.set(4, 0, 0); // Positioned on positive X axis
      frontCamera.lookAt(0, 0, 0);
      frontCamera.updateProjectionMatrix();

      // Update camera reference for screenshot
      cameraRef.current = frontCamera;
      cameraRef.current.updateProjectionMatrix();

      const originalWidth = rendererRef.current.domElement.width;
      const originalHeight = rendererRef.current.domElement.height;

      const size = Math.min(originalWidth, originalHeight);

      rendererRef.current.setSize(size, size);

      cameraRef.current.aspect = 1;
      cameraRef.current.updateProjectionMatrix();

      rendererRef.current.render(sceneRef.current, cameraRef.current);

      const dataURL = rendererRef.current.domElement.toDataURL("image/jpeg");

      const base64Data = dataURL.split(",")[1];

      const byteString = atob(base64Data);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: "image/jpeg" });

      const filename = `${
        selected_model.thumbnail_path.split("-")[0].split("/")[1]
      }-5.jpg`;

      try {
        setUploadMessage("Uploading Side-View Screenshot......");

        const response = await fetch(
          `${BASE_URL_FLASK}/upload_screenshot?filename=${encodeURIComponent(
            filename
          )}`
        );
        if (!response.ok) {
          throw new Error("Failed to get upload URL");
        }

        const { url } = await response.json();

        await uploadWithProgress(url, blob, "image/jpeg");

        setUploadProgress(100);
        setUploadMessage("Screenshot uploaded successfully");
      } catch (error) {
        console.error("Error uploading screenshot:", error);
        setUploadMessage(`Screenshot upload failed: ${error.message}`);
        setTimeout(() => {
          setIsUploading(false);
          setUploadMessage("");
        }, 2000);
        return;
      }

      rendererRef.current.setSize(originalWidth, originalHeight);

      cameraRef.current.aspect = originalWidth / originalHeight;
      cameraRef.current.updateProjectionMatrix();

      if (attachedControl && modelRef.current) {
        attachedControl.attach(modelRef.current);
      }
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    setUploadProgress(0);
    setUploadMessage("Preparing model for upload...");

    try {
      const parent = new THREE.Object3D();
      parent.position.set(0, 0, 0);
      const model = modelRef.current.clone();
      parent.add(model);
      parent.updateMatrixWorld(true);

      const worldPos = new THREE.Vector3();
      model.getWorldPosition(worldPos);
      console.log("Exact model position:", worldPos);

      const boundingBox = new THREE.Box3().setFromObject(parent);
      const delta = new THREE.Vector3(
        model.position.x / 2,
        model.position.y / 2,
        model.position.z / 2
      );
      const min = boundingBox.min;
      const max = boundingBox.max;
      const boundingBoxData = {
        right: Math.abs((min.x - delta.x).toFixed(3)),
        forward: Math.abs((min.z - delta.z).toFixed(3)),
        left: Math.abs((max.x - delta.x).toFixed(3)),
        backward: Math.abs((max.z - delta.z).toFixed(3)),
        upward: Math.abs((max.y - delta.y).toFixed(3)),
      };

      setUploadMessage("Calculating polygon count...");
      let triangleCount = 0;
      model.traverse((child) => {
        if (child.isMesh && child.geometry) {
          if (child.geometry.isBufferGeometry) {
            if (child.geometry.index) {
              triangleCount += child.geometry.index.count / 3;
            } else if (child.geometry.attributes.position) {
              triangleCount += child.geometry.attributes.position.count / 3;
            }
          } else if (child.geometry.faces) {
            triangleCount += child.geometry.faces.length;
          }
        }
      });
      setUploadMessage(`Model has ${triangleCount} triangles`);

      try {
        setUploadMessage("Updating model metadata...");
        const metadataPayload = {
          model_id: selected_model.model_id,
          extents: boundingBoxData,
          poly_count: triangleCount,
          username: username,
        };
        const metadataResponse = await fetch(
          `${BASE_URL_NODE}/web-api/update_model_metadata`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(metadataPayload),
          }
        );
        if (!metadataResponse.ok) {
          const errorData = await metadataResponse.json();
          throw new Error(
            `Failed to update model metadata: ${
              errorData.error || "Unknown error"
            }`
          );
        }
      } catch (error) {
        console.error("Error updating model metadata:", error);
        setUploadMessage(`Metadata update failed: ${error.message}`);
        setTimeout(() => {
          setIsUploading(false);
          setUploadMessage("");
        }, 3000);
        return;
      }

      const exporter = new GLTFExporter();
      const binary = await new Promise((resolve, reject) => {
        exporter.parse(parent, resolve, reject, { binary: true });
      });

      const blob = new Blob([binary], { type: "application/octet-stream" });
      const originalFilename =
        model_source.split("/").pop().split("?")[0] || "model.glb";
      const filename = `aligned/${originalFilename}`;

      setUploadMessage("Getting upload URL from server...");
      const response = await fetch(
        `${BASE_URL_FLASK}/upload_model?filename=${encodeURIComponent(
          filename
        )}`
      );
      if (!response.ok) throw new Error("Failed to get upload URL");
      const { url } = await response.json();
      if (!url) throw new Error("No upload URL provided by server");

      setUploadMessage("Uploading model...");
      await uploadWithProgress(url, blob);

      setUploadMessage("Upload completed successfully!");
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Clear scene and controls safely
      try {
        if (sceneRef.current && modelRef.current) {
          // First detach all transform controls
          [
            controlsRef.current?.transformControlsPersp,
            controlsRef.current?.transformControlsSide,
            controlsRef.current?.transformControlsTop,
            controlsRef.current?.transformControlsFront,
          ].forEach((control) => {
            if (control && control.object === modelRef.current) {
              control.detach();
            }
          });

          // Remove current model
          if (modelRef.current.parent) {
            modelRef.current.traverse((child) => {
              if (child.geometry) child.geometry.dispose();
              if (child.material) {
                if (Array.isArray(child.material)) {
                  child.material.forEach((m) => m.dispose());
                } else {
                  child.material.dispose();
                }
              }
            });
            modelRef.current.parent.remove(modelRef.current);
          }

          // Clear the reference
          modelRef.current = null;
        }

        console.log("✅ Scene cleared successfully");
      } catch (error) {
        console.error("Error clearing scene:", error);
      }

      // Get the verification URL
      setUploadMessage("Getting verification URL...");
      const verifyResponse = await fetch(
        `${BASE_URL_FLASK}/models/${originalFilename}?aligned=true`
      );
      if (!verifyResponse.ok) {
        throw new Error("Failed to get verification URL");
      }
      const verifyUrl = await verifyResponse.text();

      // Show loading overlay
      const verificationOverlay = document.createElement("div");
      verificationOverlay.className =
        "fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50";
      verificationOverlay.innerHTML = `
        <div class="bg-gray-800 p-6 rounded-lg max-w-md w-full text-center">
          <h3 class="text-xl font-bold text-green-400 mb-4">Opening Model</h3>
          <p class="text-white mb-4">Loading from aligned storage...</p>
          <p class="text-yellow-400 mb-4 font-mono text-sm">aligned/${originalFilename}</p>
          <div class="animate-spin text-green-400 text-2xl mb-4">⟳</div>
        </div>
      `;
      document.body.appendChild(verificationOverlay);

      // Now load the aligned model with the verified URL
      new GLTFLoader().load(
        verifyUrl,
        (gltf) => {
          const savedModel = gltf.scene;
          savedModel.updateMatrixWorld(true);
          console.log("🔍 Server URL:", verifyUrl);
          // Add model to scene
          if (sceneRef.current) {
            modelRef.current = savedModel;
            sceneRef.current.add(modelRef.current);

            // Attach controls to new model
            [
              controlsRef.current?.transformControlsPersp,
              controlsRef.current?.transformControlsSide,
              controlsRef.current?.transformControlsTop,
              controlsRef.current?.transformControlsFront,
            ].forEach((control) => {
              if (control && modelRef.current) control.attach(modelRef.current);
            });
          }

          // Remove loading overlay
          document.body.removeChild(verificationOverlay);
          setIsUploading(false);
          setUploadMessage("");

          // Auto-close the model viewer after successful upload
          // setTimeout(() => {
          //   close();
          // }, 1000);
        },
        (xhr) => {
          const percent = Math.round((xhr.loaded / xhr.total) * 100);
          verificationOverlay.querySelector(
            ".text-green-400"
          ).textContent = `Loading model: ${percent}%`;
        },
        (error) => {
          console.error("Error loading model:", error);
          verificationOverlay.innerHTML = `
            <div class="bg-gray-800 p-6 rounded-lg max-w-md w-full text-center">
              <h3 class="text-xl font-bold text-red-500 mb-4">⚠ Loading Failed</h3>
              <p class="text-white mb-4">${error.message}</p>
              <button class="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700" 
                      onclick="this.parentElement.parentElement.remove()">
                Close
              </button>
            </div>
          `;
        }
      );
    } catch (error) {
      console.error("Error uploading model:", error);
      setUploadMessage(`Upload failed: ${error.message}`);
      setTimeout(() => {
        setIsUploading(false);
        setUploadMessage("");
      }, 3000);
    }
  };

  const uploadWithProgress = async (
    signedUrl,
    blob,
    contentType = "application/octet-stream"
  ) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round(
            (event.loaded / event.total) * 100
          );
          setUploadProgress(percentComplete);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          console.error("Upload failed with status:", xhr.status);
          console.error("Response:", xhr.responseText);
          reject(new Error(`HTTP error ${xhr.status}: ${xhr.responseText}`));
        }
      };

      xhr.onerror = (e) => {
        console.error("XHR error:", e);
        reject(new Error("Network error occurred"));
      };

      xhr.ontimeout = () => reject(new Error("Upload timed out"));

      xhr.open("PUT", signedUrl, true);
      xhr.setRequestHeader("Content-Type", contentType);
      xhr.send(blob);
    });
  };

  const captureAndUploadPreviews = async () => {
    if (!modelRef.current) {
      alert("No model to capture previews for");
      return;
    }
    setIsCapturingPreviews(true);
    setPreviewProgress(0);
    setPreviewMessage("Starting preview capture...");

    const angles = [360, 90, 180, 270];
    const originalRotation = modelRef.current.rotation.y;

    const objectsToHide = [];

    try {
      sceneRef.current.traverse((child) => {
        if (
          child === mannyRef.current ||
          (mannyRef.current && mannyRef.current.children.includes(child))
        ) {
          objectsToHide.push({ object: child, originalVisible: child.visible });
        }
        if (
          child.isGridHelper ||
          child.name === "grid" ||
          child.type === "GridHelper"
        ) {
          objectsToHide.push({ object: child, originalVisible: child.visible });
        }
        if (
          child.isMesh &&
          child.geometry &&
          child.geometry.type === "SphereGeometry" &&
          child !== modelRef.current
        ) {
          objectsToHide.push({ object: child, originalVisible: child.visible });
        }
      });

      Object.values(gizmosRef.current).forEach((gizmo) => {
        if (gizmo) {
          objectsToHide.push({ object: gizmo, originalVisible: gizmo.visible });
        }
      });

      const modelUid = selected_model.uid || selected_model.model_id;

      const uploadedPreviews = [];

      for (let i = 0; i < angles.length; i++) {
        modelRef.current.rotation.y = THREE.MathUtils.degToRad(angles[i]);
        modelRef.current.updateMatrixWorld(true);

        setPreviewProgress((i / angles.length) * 100);
        setPreviewMessage(`Capturing preview ${i + 1}/4 at ${angles[i]}°...`);

        objectsToHide.forEach(({ object }) => {
          object.visible = false;
          if (object === mannyRef.current) {
            object.traverse((child) => {
              child.visible = false;
            });
          }
        });

        sceneRef.current.updateMatrixWorld(true);
        await new Promise((resolve) => setTimeout(resolve, 300));

        const screenshot = await captureCleanScreenshot768();

        const filename = `previews/${modelUid}-${i + 1}.jpg`;

        try {
          const response = await fetch(
            `${BASE_URL_FLASK}/upload_preview?filename=${encodeURIComponent(
              filename
            )}`
          );
          if (!response.ok) {
            throw new Error(
              `Failed to get preview upload URL: ${response.statusText}`
            );
          }

          const data = await response.json();
          if (!data.url) {
            throw new Error("No upload URL provided by server");
          }

          await uploadWithProgress(data.url, screenshot, "image/jpeg");
          const previewUrl = `${BASE_URL_FLASK}/previews/${modelUid}-${
            i + 1
          }.jpg?t=${Date.now()}`;
          uploadedPreviews.push(previewUrl);
        } catch (error) {
          console.error(`Error uploading preview ${i + 1}:`, error);
          throw error;
        }
      }

      setPreviewUrls(uploadedPreviews);
      setPreviewProgress(100);
      setPreviewMessage("All previews uploaded successfully!");
      console.log(selected_model, modelUid, uploadedPreviews);

      // Extract clean model ID for all models
      const currentModelId = selected_model.uid || selected_model.model_id;
      let thumnailUid;
      if (currentModelId.includes("sketchfab.com")) {
        // For Sketchfab models, extract just the model ID
        thumnailUid = currentModelId.split("https://sketchfab.com/3d-")[1];
      } else if (currentModelId.includes("models/")) {
        // Extract after 'models/' and clean any trailing path
        thumnailUid = currentModelId.split("models/")[1];
      } else {
        // Use as-is
        thumnailUid = currentModelId;
      }

      // Ensure no "models/" prefix remains
      thumnailUid = thumnailUid.replace(/^models\//, "");

      // Create thumbnail path
      const updatedThumbnailPath = `previews/${thumnailUid}-1`;

      console.log(
        "Updating context with new thumbnail_path:",
        updatedThumbnailPath
      );

      // Update context state
      const currentTimestamp = Date.now();
      console.log("Updating model context:", {
        selectedModelUid: selected_model.uid,
        selectedModelId: selected_model.model_id,
        thumbnailPath: updatedThumbnailPath,
      });

      setState((prev) => ({
        ...prev,
        selected_model: {
          ...prev.selected_model,
          thumbnail_path: updatedThumbnailPath,
          preview_updated_at: currentTimestamp,
        },
        models: prev.models.map((model) => {
          const modelId = model.uid || model.model_id;
          const selectedModelId = selected_model.uid || selected_model.model_id;
          const isMatch = modelId === selectedModelId;
          if (isMatch) {
            console.log("Updating model:", { modelId, selectedModelId, model });
          }
          return isMatch
            ? {
                ...model,
                thumbnail_path: updatedThumbnailPath,
                preview_updated_at: currentTimestamp,
              }
            : model;
        }),
      }));

      setTimeout(() => {
        setIsCapturingPreviews(false);
        setPreviewMessage("");
      }, 2000);
    } catch (error) {
      console.error("Error capturing previews:", error);
      setPreviewMessage(`Preview capture failed: ${error.message}`);
      setTimeout(() => {
        setIsCapturingPreviews(false);
        setPreviewMessage("");
      }, 3000);
    } finally {
      objectsToHide.forEach(({ object, originalVisible }) => {
        object.visible = originalVisible;
        if (object === mannyRef.current) {
          object.traverse((child) => {
            child.visible = originalVisible;
          });
        }
      });

      if (modelRef.current) {
        modelRef.current.rotation.y = originalRotation;
        modelRef.current.updateMatrixWorld(true);
      }

      sceneRef.current.updateMatrixWorld(true);
    }
  };

  const captureCleanScreenshot768 = () => {
    return new Promise((resolve) => {
      const offscreenCanvas = document.createElement("canvas");
      offscreenCanvas.width = 768;
      offscreenCanvas.height = 768;

      const offscreenRenderer = new THREE.WebGLRenderer({
        canvas: offscreenCanvas,
        antialias: true,
        preserveDrawingBuffer: true,
        alpha: false,
      });

      offscreenRenderer.setSize(768, 768);
      offscreenRenderer.setClearColor(0x3b3b3b, 1);

      const cleanScene = new THREE.Scene();
      cleanScene.background = new THREE.Color(0x3b3b3b);

      // Enhanced lighting setup for preview capture
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
      cleanScene.add(ambientLight);

      const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
      keyLight.position.set(5, 8, 5);
      cleanScene.add(keyLight);

      const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
      fillLight.position.set(-5, 5, 8);
      cleanScene.add(fillLight);

      const backLight = new THREE.DirectionalLight(0xffffff, 0.6);
      backLight.position.set(0, 3, -8);
      cleanScene.add(backLight);

      // Additional side lighting for better coverage
      const sideLight1 = new THREE.DirectionalLight(0xffffff, 0.5);
      sideLight1.position.set(8, 2, 0);
      cleanScene.add(sideLight1);

      const sideLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
      sideLight2.position.set(-8, 2, 0);
      cleanScene.add(sideLight2);

      if (modelRef.current) {
        const modelClone = modelRef.current.clone(true);
        modelClone.traverse((child) => {
          if (child.isMesh) {
            child.material = child.material.clone();
            child.material.needsUpdate = true;
            child.castShadow = false;
            child.receiveShadow = false;
          }
        });
        cleanScene.add(modelClone);
      }

      const screenshotCamera = new THREE.PerspectiveCamera(45, 1, 0.1, 1000);

      if (modelRef.current) {
        const boundingBox = new THREE.Box3().setFromObject(modelRef.current);
        const size = boundingBox.getSize(new THREE.Vector3());
        const center = boundingBox.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const distance = maxDim * 1.75;

        screenshotCamera.position.set(
          center.x + distance * 0.7,
          center.y + distance * 0.5,
          center.z + distance * 0.7
        );
        screenshotCamera.lookAt(center);
      }

      screenshotCamera.updateProjectionMatrix();

      offscreenRenderer.render(cleanScene, screenshotCamera);

      offscreenCanvas.toBlob(
        (blob) => {
          offscreenRenderer.dispose();
          cleanScene.traverse((object) => {
            if (object.geometry) object.geometry.dispose();
            if (object.material) {
              if (Array.isArray(object.material)) {
                object.material.forEach((m) => m.dispose());
              } else {
                object.material.dispose();
              }
            }
          });

          resolve(blob);
        },
        "image/jpeg",
        0.95
      );
    });
  };

  useEffect(() => {
    if (isUploading) {
      const handleBeforeUnload = (e) => {
        const message =
          "Upload in progress! Leaving now will cancel your upload.";
        e.returnValue = message;
        return message;
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [isUploading]);

  return (
    <>
      <div
        ref={containerRef}
        style={{
          width: "96vw",
          height: "90vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "rgba(0,0,0,0.5)",
            color: "#fff",
            fontFamily: "monospace",
            padding: "5px 10px",
            fontSize: "1.2em",
          }}
          className="rounded-lg drop-shadow-lg border border-red-400"
        >
          <div>
            <strong>Controls</strong>
          </div>
          <div>1: Perspective View</div>
          <div>2: Side View</div>
          <div>3: Top View</div>
          <div>4: Front View</div>
          <div>Q: Gizmo Translate</div>
          <div>W: Gizmo Rotate</div>
          <div>E: Gizmo Scale</div>
          <div>R: Scale</div>
          <div>A: Translate X</div>
          <div>S: Translate Y</div>
          <div>D: Translate Z</div>
          <div>Z: Rotate X</div>
          <div>X: Rotate Y</div>
          <div>C: Rotate Z</div>
          <div>Ctrl+Z: Undo</div>
          <div>Enter: Apply</div>
          <div>Tilde(`): Cancel</div>
        </div>
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            background: "rgba(0,0,0,0.5)",
            color: "#fff",
            fontFamily: "monospace",
            padding: "5px 10px",
            fontSize: "1.2em",
          }}
          className="rounded-lg drop-shadow-lg border border-red-400"
        >
          {debugText}
        </div>
        {transformAction && (
          <div
            style={{
              position: "absolute",
              top: "60px",
              left: "10px",
              color: "#fff",
            }}
            className="rounded-sm drop-shadow-lg border border-yellow-400 px-2"
          >
            {transformAction}
          </div>
        )}

        {isUploading && (
          <div
            className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center z-50"
            style={{ pointerEvents: "all" }}
          >
            <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full">
              <h3 className="text-xl font-bold text-white mb-2">
                {uploadMessage}
              </h3>

              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>

              <p className="text-white text-center font-bold">
                {uploadProgress}%
              </p>

              <div className="mt-4 bg-yellow-600 p-3 rounded-md">
                <p className="text-white text-sm">
                  <span className="font-bold">⚠️ Warning:</span> Please do not
                  close this page or navigate away until the upload completes.
                </p>
              </div>
            </div>
          </div>
        )}

        {isCapturingPreviews && (
          <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center z-50">
            <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full">
              <h3 className="text-xl font-bold text-white mb-2">
                {previewMessage}
              </h3>

              <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                <div
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${previewProgress}%` }}
                ></div>
              </div>

              <p className="text-white text-center font-bold">
                {Math.round(previewProgress)}%
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-2 absolute bottom-5 right-5 text-xl">
          <button
            className="bg-gray-400 px-2 rounded font-bold hover:bg-gray-700"
            onClick={close}
            disabled={isUploading || isCapturingPreviews}
          >
            Close
          </button>
          <button
            className="bg-blue-400 px-2 rounded font-bold hover:bg-blue-700"
            onClick={captureAndUploadPreviews}
            disabled={isUploading || isCapturingPreviews}
          >
            Capture Previews
          </button>
          <button
            className="bg-purple-400 px-2 rounded font-bold hover:bg-purple-700"
            onClick={uploadModel}
            disabled={isUploading || isCapturingPreviews}
          >
            Upload
          </button>
        </div>
        {previewUrls.length > 0 && (
          <div className="absolute bottom-20 left-5 bg-gray-800 p-4 rounded-lg">
            <h4 className="text-white mb-2">Updated Previews:</h4>
            <div className="flex gap-2">
              {previewUrls.map((url, index) => {
                let finalUrl = url;

                // Remove localhost API prefix if present
                // const apiPrefix = "http://localhost:8070/api/";
                // if (finalUrl.startsWith(apiPrefix)) {
                //   finalUrl = finalUrl.replace(apiPrefix, ""); // leaves: previews/https://sketchfab.com/...
                // }

                // Remove embedded Sketchfab path if present
                const sketchfabEmbed = "https://sketchfab.com/3d-models/";
                if (finalUrl.includes(sketchfabEmbed)) {
                  finalUrl = finalUrl.replace(sketchfabEmbed, ""); // leaves: previews/010c4866d8ad40ee975e6f123320b69c-1.jpg?t=...
                }

                return (
                  <img
                    key={index}
                    src={finalUrl}
                    alt={`Preview ${index + 1}`}
                    className="w-24 h-24 object-cover rounded"
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
